import { z } from 'zod';

/** Every non-2xx API response body. `code` is a stable machine code; web maps it to i18n "errors:<code>". */
export const ErrorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
    requestId: z.string().optional(),
  }),
});
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

/** Stable error codes (extend in your module's contract file, never rename). */
export const ErrorCode = {
  VALIDATION: 'VALIDATION',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  ROUND_LOCKED: 'ROUND_LOCKED',
  EDIT_WINDOW_EXPIRED: 'EDIT_WINDOW_EXPIRED',
  NOT_ASSIGNED: 'NOT_ASSIGNED',
  NOT_ELIGIBLE: 'NOT_ELIGIBLE',
  ADMIN_LOCKED: 'ADMIN_LOCKED',
  OP_ID_REUSED: 'OP_ID_REUSED',
  SIGN_OFF_CODE_INVALID: 'SIGN_OFF_CODE_INVALID',
  PIN_RESET_REQUIRED: 'PIN_RESET_REQUIRED',
  LEADERBOARD_HIDDEN: 'LEADERBOARD_HIDDEN',
  VIEW_HIDDEN: 'VIEW_HIDDEN',
  TEE_NOT_ON_ROUND_COURSE: 'TEE_NOT_ON_ROUND_COURSE',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  TOURNAMENT_FINALIZED: 'TOURNAMENT_FINALIZED',
  REASON_REQUIRED: 'REASON_REQUIRED',
  VERSION_CONFLICT: 'VERSION_CONFLICT',
  INVALID_STATE: 'INVALID_STATE',
  INTERNAL: 'INTERNAL',
} as const;
export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

export const PageQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
});
export const pageOf = <T extends z.ZodType>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable() });

export const IdParamSchema = z.object({ id: z.uuid() });
export const OkSchema = z.object({ ok: z.literal(true) });

/**
 * POST /api/v1/telemetry/client-errors — error beacon of the web app / caddy PWA (render crashes,
 * unhandled errors, offline-queue and service-worker failures). Forwarded to Sentry when the api
 * has SENTRY_DSN, always logged.
 */
export const ClientErrorKind = z.enum(['error', 'unhandledrejection', 'render', 'queue', 'sw']);
export const ClientErrorReportSchema = z.object({
  kind: ClientErrorKind,
  message: z.string().trim().min(1).max(2000),
  stack: z.string().max(8000).optional(),
  /** page path without query string (tokens never leave the device) */
  path: z.string().max(300).optional(),
  release: z.string().max(80).optional(),
  userAgent: z.string().max(400).optional(),
  /** small string map, e.g. { queue: 'caddy', pending: '3' } */
  context: z.record(z.string().max(40), z.string().max(300)).optional(),
});
export type ClientErrorReport = z.infer<typeof ClientErrorReportSchema>;

/**
 * Share-link tokens are path segments (/p/:playerToken, /c/:caddyToken, /t/:publicToken,
 * /a/:adminToken, /r/:regToken, /join/:inviteToken, api /public/t/:token, /club-invites/:token).
 * A player access token is a credential, so client error reports must never carry one: the web
 * reporter and the api telemetry route both pass path / message / stack through this
 * (CLAUDE.md §15 #329). Also masks `Bearer <token>` and token-like query parameters.
 */
const LINK_TOKEN_SEGMENT = /(\/(?:p|c|t|a|r|join|club-invites)\/)(?!:token\b)[^/?#\s"'`)<>]+/g;
const BEARER = /\b(Bearer\s+)[A-Za-z0-9._~+/=-]+/gi;
const TOKEN_PARAM = /([?&](?:token|accessToken|access_token|code|lastEventId)=)[^&#\s"'`)<>]+/gi;

export function redactLinkTokens(text: string): string {
  return text
    .replace(LINK_TOKEN_SEGMENT, '$1:token')
    .replace(BEARER, '$1[redacted]')
    .replace(TOKEN_PARAM, '$1[redacted]');
}
